import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  X, Loader2, User as UserIcon, Calendar as CalendarIcon, Clock, Flag, Paperclip,
  MessageSquare, CheckCircle2, RotateCcw, Check, Upload, Trash2, ListChecks, Play,
  Repeat, ShieldCheck, Send, ChevronRight, Star, Hand, Pause, Link2, AlertTriangle,
} from 'lucide-react';
import {
  getTaskDetail, updateTask, reassignTask, acceptTask, approveTask, rejectTask,
  addTaskComment, deleteTaskComment, addTaskAttachment, deleteTaskAttachment,
  listUsers, apiClient, API, rateTask, listTaskRatings, claimTask, requestSnooze,
  decideSnooze, startTimer, stopTimer, listTasks, listDepartments, markTaskCommentsRead,
} from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const PRI_META = {
  urgent: { color: 'bg-red-600',   text: 'text-red-700',   ring: 'ring-red-200' },
  high:   { color: 'bg-orange-500', text: 'text-orange-700', ring: 'ring-orange-200' },
  medium: { color: 'bg-[#0f5c3d]', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  low:    { color: 'bg-gray-400',  text: 'text-gray-600',   ring: 'ring-gray-200' },
};

const STATUS_LABELS = {
  todo: 'To Do', in_progress: 'In Progress', review: 'Awaiting Approval', done: 'Done',
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const TaskDetailDialog = ({ taskId, onClose, onChanged }) => {
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [uploading, setUploading] = useState(false);
  const [ratings, setRatings] = useState([]);
  const [ratingScore, setRatingScore] = useState(5);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSaving, setRatingSaving] = useState(false);
  // Office CRM P1 additions
  const [allTasks, setAllTasks] = useState([]);
  const [depts, setDepts] = useState([]);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [snoozeForm, setSnoozeForm] = useState({ new_due_date: '', reason: '' });
  const [depPickerOpen, setDepPickerOpen] = useState(false);
  const [tickTime, setTickTime] = useState(0);   // for live timer re-render
  // Mention picker + visibility for task comments
  const [mentionQuery, setMentionQuery] = useState(null); // { text, start } when @ typed
  const [mentionIdx, setMentionIdx] = useState(0);
  const [commentMentions, setCommentMentions] = useState([]); // user_ids explicitly picked
  const [commentVisibility, setCommentVisibility] = useState('everyone'); // 'everyone' | 'selected'
  const [commentVisibleTo, setCommentVisibleTo] = useState([]); // user_ids allowed to see (for 'selected')
  const commentRef = React.useRef(null);

  // Re-render every 30s so the running timer text updates
  useEffect(() => {
    const int = setInterval(() => setTickTime((t) => t + 1), 30000);
    return () => clearInterval(int);
  }, []);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const [t, u, r, all, d] = await Promise.all([
        getTaskDetail(taskId),
        listUsers().catch(() => ({ data: [] })),
        listTaskRatings(taskId).catch(() => ({ data: [] })),
        listTasks({}).catch(() => ({ data: [] })),
        listDepartments().catch(() => ({ data: [] })),
      ]);
      setTask(t);
      setUsers(u.data || []);
      setRatings(r.data || []);
      setAllTasks(all.data || []);
      setDepts(d.data || []);
      // Fire-and-forget: mark comments as read for the current user
      markTaskCommentsRead(taskId).catch(() => {});
    } catch {
      toast({ title: 'Failed to load task', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [taskId]);
  useEffect(() => { load(); }, [load]);

  const userLookup = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.user_id] = u; });
    return m;
  }, [users]);

  if (!taskId) return null;

  const isAssignee = task && user && task.assignee_id === user.user_id;
  const isAssignor = task && user && task.created_by === user.user_id;
  const canApprove = isAssignor || ['super_admin', 'admin'].includes(user?.role);

  const doChange = async (patch, opts = { toast: true }) => {
    try {
      const updated = await updateTask(taskId, patch);
      setTask((t) => ({ ...t, ...updated }));
      if (opts.toast !== false) toast({ title: 'Saved' });
      onChanged?.();
    } catch {
      toast({ title: 'Save failed', variant: 'destructive' });
    }
  };

  const doAccept = async () => {
    try {
      const updated = await acceptTask(taskId);
      setTask((t) => ({ ...t, ...updated }));
      toast({ title: 'Task accepted' });
      onChanged?.();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Accept failed', variant: 'destructive' });
    }
  };

  const doMarkDone = async () => {
    try {
      const updated = await updateTask(taskId, { status: 'done' });
      setTask((t) => ({ ...t, ...updated }));
      toast({
        title: updated.status === 'review'
          ? 'Submitted for approval'
          : 'Task marked done',
      });
      onChanged?.();
    } catch {
      toast({ title: 'Failed', variant: 'destructive' });
    }
  };

  const doApprove = async () => {
    try {
      const updated = await approveTask(taskId);
      setTask((t) => ({ ...t, ...updated }));
      toast({ title: 'Task approved & closed' });
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Approve failed', variant: 'destructive' });
    }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) return;
    try {
      const updated = await rejectTask(taskId, rejectReason.trim());
      setTask((t) => ({ ...t, ...updated }));
      setRejectOpen(false);
      setRejectReason('');
      toast({ title: 'Sent back for rework' });
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Reject failed', variant: 'destructive' });
    }
  };

  const doReassign = async () => {
    if (!reassignTo) return;
    try {
      const updated = await reassignTask(taskId, { assignee_id: reassignTo, reason: reassignReason });
      setTask((t) => ({ ...t, ...updated }));
      setReassignOpen(false);
      setReassignReason('');
      setReassignTo('');
      toast({ title: 'Task reassigned' });
      onChanged?.();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Reassign failed', variant: 'destructive' });
    }
  };

  const doComment = async () => {
    const body = comment.trim();
    if (!body) return;
    try {
      const opts = { mentions: commentMentions };
      if (commentVisibility === 'selected') {
        // Union of picker-selected recipients and any @mentioned users
        opts.visible_to_ids = Array.from(new Set([...commentVisibleTo, ...commentMentions]));
      }
      const c = await addTaskComment(taskId, body, opts);
      setTask((t) => ({ ...t, comments: [...(t.comments || []), c] }));
      setComment('');
      setCommentMentions([]);
      setCommentVisibility('everyone');
      setCommentVisibleTo([]);
      setMentionQuery(null);
    } catch {
      toast({ title: 'Comment failed', variant: 'destructive' });
    }
  };

  const doDeleteComment = async (cid) => {
    try {
      await deleteTaskComment(taskId, cid);
      setTask((t) => ({ ...t, comments: (t.comments || []).filter((c) => c.id !== cid) }));
    } catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const doUpload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await apiClient.post('/uploads?preset=news', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        await addTaskAttachment(taskId, {
          url: res.data.url,
          thumb_url: res.data.thumb_url,
          filename: file.name,
          size: res.data.size,
          content_type: res.data.content_type,
        });
      }
      toast({ title: `${files.length} file${files.length > 1 ? 's' : ''} uploaded` });
      load();
    } catch {
      toast({ title: 'Upload failed', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const doDeleteAttachment = async (aid) => {
    try {
      await deleteTaskAttachment(taskId, aid);
      setTask((t) => ({ ...t, attachments: (t.attachments || []).filter((a) => a.id !== aid) }));
    } catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const submitRating = async () => {
    setRatingSaving(true);
    try {
      const r = await rateTask(taskId, ratingScore, ratingComment.trim());
      setRatings((prev) => [r, ...prev.filter((x) => x.id !== r.id)]);
      setRatingComment('');
      toast({ title: `Rated ${ratingScore}★` });
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Rating failed', variant: 'destructive' });
    } finally { setRatingSaving(false); }
  };

  // ---- Office CRM P1 actions ----
  const doClaim = async () => {
    try {
      const updated = await claimTask(taskId);
      setTask((t) => ({ ...t, ...updated }));
      toast({ title: 'Claimed — task is yours' });
      onChanged?.();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Claim failed', variant: 'destructive' }); }
  };

  const doStartStopTimer = async () => {
    try {
      if (task.active_timer) {
        const r = await stopTimer(taskId);
        toast({ title: `Timer stopped · ${r.minutes} min logged` });
      } else {
        await startTimer(taskId);
        toast({ title: 'Timer running' });
      }
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Timer failed', variant: 'destructive' }); }
  };

  const doRequestSnooze = async () => {
    if (!snoozeForm.new_due_date) return toast({ title: 'Pick a new date', variant: 'destructive' });
    try {
      await requestSnooze(taskId, snoozeForm);
      setSnoozeOpen(false);
      setSnoozeForm({ new_due_date: '', reason: '' });
      toast({ title: 'Snooze request sent to assignor' });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  const doDecideSnooze = async (reqId, decision) => {
    try {
      await decideSnooze(taskId, { request_id: reqId, decision });
      toast({ title: `Snooze ${decision}` });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  const toggleDependency = async (otherId) => {
    const cur = task.depends_on_task_ids || [];
    const next = cur.includes(otherId) ? cur.filter((i) => i !== otherId) : [...cur, otherId];
    await doChange({ depends_on_task_ids: next }, { toast: false });
  };

  const runningMinutes = task?.active_timer
    ? Math.max(1, Math.floor((Date.now() - new Date(task.active_timer.started_at).getTime()) / 60000))
    : 0;

  const canClaim = task?.assign_scope === 'role' && !task.claimed_by && task.role_dept_id;
  const isAssigneeAny = task && user && (
    task.assignee_id === user.user_id ||
    (task.assignee_ids || []).includes(user.user_id) ||
    task.claimed_by === user.user_id
  );
  const pendingSnoozes = (task?.snooze_requests || []).filter((r) => r.status === 'pending');

  const doToggleChecklist = async (itemId) => {
    const nextList = (task.checklist || []).map((it) => it.id === itemId ? { ...it, done: !it.done } : it);
    await doChange({ checklist: nextList }, { toast: false });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/50" data-testid="task-detail-dialog">
      <div className="bg-white w-full max-w-5xl max-h-[95vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between bg-[#0b1a2e] text-white">
          <div className="flex items-center gap-3 min-w-0">
            <ListChecks className="w-5 h-5 opacity-80 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-white/60">Task</div>
              <div className="text-lg font-medium truncate">{task?.title || '…'}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded" aria-label="close" data-testid="task-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !task ? (
          <div className="p-16 text-center text-[#0b1a2e]/50 flex-1">
            <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading task…
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3">
              {/* LEFT — description + checklist + comments */}
              <div className="col-span-2 p-6 border-b lg:border-b-0 lg:border-r border-black/5">
                {/* Actions bar */}
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  {task.status === 'todo' && isAssignee && !task.accepted_at && (
                    <button onClick={doAccept} data-testid="task-accept-btn"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold hover:bg-[#0b4a30]">
                      <Play className="w-3.5 h-3.5" /> Accept & Start
                    </button>
                  )}
                  {task.status === 'in_progress' && isAssignee && (
                    <button onClick={doMarkDone} data-testid="task-done-btn"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold hover:bg-[#0b4a30]">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {task.requires_approval ? 'Submit for Approval' : 'Mark Done'}
                    </button>
                  )}
                  {task.status === 'review' && canApprove && (
                    <>
                      <button onClick={doApprove} data-testid="task-approve-btn"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold hover:bg-[#0b4a30]">
                        <ShieldCheck className="w-3.5 h-3.5" /> Approve & Close
                      </button>
                      <button onClick={() => setRejectOpen(true)} data-testid="task-reject-btn"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-red-300 text-red-700 text-xs font-semibold hover:bg-red-50">
                        <RotateCcw className="w-3.5 h-3.5" /> Send Back
                      </button>
                    </>
                  )}
                  {task.status === 'done' && (
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Completed
                    </span>
                  )}
                  <button onClick={() => setReassignOpen((o) => !o)} data-testid="task-reassign-btn"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#0f5c3d]/20 text-[#0b1a2e] text-xs font-medium hover:bg-[#f7fbf8]">
                    <UserIcon className="w-3.5 h-3.5" /> Reassign
                  </button>
                  {canClaim && (
                    <button onClick={doClaim} data-testid="task-claim-btn"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600">
                      <Hand className="w-3.5 h-3.5" /> Claim
                    </button>
                  )}
                  {isAssigneeAny && task.status !== 'done' && (
                    <button onClick={doStartStopTimer} data-testid="task-timer-btn"
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${task.active_timer ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-white border border-[#0f5c3d]/20 text-[#0f5c3d] hover:bg-[#f7fbf8]'}`}>
                      {task.active_timer ? <><Pause className="w-3.5 h-3.5" /> Stop · {runningMinutes}m</> : <><Play className="w-3.5 h-3.5" /> Start timer</>}
                    </button>
                  )}
                  {isAssigneeAny && task.status !== 'done' && task.due_date && (
                    <button onClick={() => setSnoozeOpen((o) => !o)} data-testid="task-snooze-btn"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#0f5c3d]/20 text-[#0b1a2e] text-xs font-medium hover:bg-[#f7fbf8]">
                      <Clock className="w-3.5 h-3.5" /> Request snooze
                    </button>
                  )}
                  {isAssigneeAny && task.status !== 'done' && task.status !== 'snoozed' && (
                    <button onClick={async () => {
                      if (!window.confirm('Pause this task? It will move out of My Day and Workload until resumed.')) return;
                      try {
                        const upd = await updateTask(taskId, { status: 'snoozed' });
                        setTask((t) => ({ ...t, ...upd }));
                        toast({ title: 'Task paused', description: 'Resume it any time.' });
                      } catch { toast({ title: 'Pause failed', variant: 'destructive' }); }
                    }} data-testid="task-pause-btn"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold hover:bg-purple-200">
                      <Pause className="w-3.5 h-3.5" /> Pause task
                    </button>
                  )}
                  {task.status === 'snoozed' && (
                    <button onClick={async () => {
                      try {
                        const upd = await updateTask(taskId, { status: 'todo' });
                        setTask((t) => ({ ...t, ...upd }));
                        toast({ title: 'Task resumed', description: 'Back on your active queue.' });
                      } catch { toast({ title: 'Resume failed', variant: 'destructive' }); }
                    }} data-testid="task-resume-btn"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold hover:bg-[#0b1a2e]">
                      <Play className="w-3.5 h-3.5" /> Resume task
                    </button>
                  )}
                </div>

                {snoozeOpen && (
                  <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="task-snooze-panel">
                    <div className="text-xs font-semibold text-amber-800 uppercase tracking-widest mb-2">Snooze request (needs assignor OK)</div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <input type="datetime-local" value={snoozeForm.new_due_date}
                        onChange={(e) => setSnoozeForm({ ...snoozeForm, new_due_date: e.target.value })}
                        data-testid="snooze-date"
                        className="px-2 py-1.5 border border-amber-300 rounded text-sm bg-white flex-1 min-w-[200px]" />
                    </div>
                    <textarea value={snoozeForm.reason} onChange={(e) => setSnoozeForm({ ...snoozeForm, reason: e.target.value })}
                      placeholder="Why do you need more time?"
                      className="w-full mb-2 px-3 py-2 border border-amber-300 rounded text-sm bg-white" rows={2} />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setSnoozeOpen(false)} className="px-3 py-1.5 text-xs">Cancel</button>
                      <button onClick={doRequestSnooze} data-testid="snooze-submit"
                        className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded-full font-semibold">Send request</button>
                    </div>
                  </div>
                )}

                {/* Pending snooze decisions (visible to assignor) */}
                {pendingSnoozes.length > 0 && (task?.created_by === user?.user_id || ['super_admin', 'admin'].includes(user?.role)) && (
                  <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="pending-snoozes-panel">
                    <div className="text-xs font-semibold text-amber-800 uppercase tracking-widest mb-2">Pending snooze decisions</div>
                    <div className="space-y-2">
                      {pendingSnoozes.map((r) => (
                        <div key={r.id} className="flex items-center gap-2 text-sm">
                          <div className="flex-1 min-w-0">
                            <b>{r.requested_by_name}</b> wants due date → <b>{r.new_due_date?.slice(0, 10)}</b>
                            {r.reason && <span className="italic text-[#0b1a2e]/60"> — "{r.reason}"</span>}
                          </div>
                          <button onClick={() => doDecideSnooze(r.id, 'approved')} className="px-2 py-1 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold">Approve</button>
                          <button onClick={() => doDecideSnooze(r.id, 'denied')} className="px-2 py-1 rounded-full bg-white border border-red-300 text-red-700 text-xs">Deny</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dependencies */}
                <div className="mb-5" data-testid="task-dependencies">
                  <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-2 flex items-center gap-1">
                    <Link2 className="w-3.5 h-3.5" /> Depends on
                    <button onClick={() => setDepPickerOpen((o) => !o)}
                      className="ml-2 text-[10px] font-normal normal-case tracking-normal text-[#0f5c3d] hover:underline">
                      {depPickerOpen ? 'Done' : '+ Add dependency'}
                    </button>
                  </div>
                  {(task.depends_on_task_ids || []).length === 0 && !depPickerOpen && (
                    <div className="text-xs text-[#0b1a2e]/50 italic">No dependencies — this task can start any time.</div>
                  )}
                  {(task.depends_on_task_ids || []).map((did) => {
                    const dep = allTasks.find((t) => t.id === did);
                    if (!dep) return null;
                    const isDone = dep.status === 'done';
                    return (
                      <div key={did} className={`flex items-center gap-2 text-xs p-1.5 rounded ${isDone ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                        {isDone ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-700" />}
                        <span className={`flex-1 truncate ${isDone ? 'line-through text-[#0b1a2e]/50' : 'text-[#0b1a2e]'}`}>{dep.title}</span>
                        <span className="text-[10px] text-[#0b1a2e]/50">{dep.status}</span>
                        <button onClick={() => toggleDependency(did)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    );
                  })}
                  {depPickerOpen && (
                    <div className="mt-2 p-2 bg-[#f7fbf8] rounded max-h-40 overflow-y-auto space-y-1">
                      {allTasks.filter((t) => t.id !== taskId && !(task.depends_on_task_ids || []).includes(t.id)).slice(0, 30).map((t) => (
                        <button key={t.id} onClick={() => toggleDependency(t.id)}
                          className="w-full text-left text-xs p-1.5 rounded hover:bg-white flex items-center gap-2">
                          <span className="text-[#0b1a2e] truncate">{t.title}</span>
                          <span className="ml-auto text-[10px] text-[#0b1a2e]/50">{t.status}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Description */}

                {reassignOpen && (
                  <div className="mb-5 p-3 bg-[#f7fbf8] border border-[#0f5c3d]/15 rounded-lg" data-testid="task-reassign-panel">
                    <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-2">Reassign task</div>
                    <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}
                      data-testid="task-reassign-user"
                      className="w-full mb-2 px-3 py-2 border border-slate-300 rounded text-sm bg-white">
                      <option value="">Choose new assignee…</option>
                      {users.filter((u) => u.user_id !== task.assignee_id).map((u) => (
                        <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>
                      ))}
                    </select>
                    <textarea value={reassignReason} onChange={(e) => setReassignReason(e.target.value)}
                      placeholder="Reason (optional, saved to history)"
                      data-testid="task-reassign-reason"
                      className="w-full mb-2 px-3 py-2 border border-slate-300 rounded text-sm bg-white" rows={2} />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setReassignOpen(false)} className="px-3 py-1.5 text-xs text-[#0b1a2e]/70 hover:text-[#0b1a2e]">Cancel</button>
                      <button onClick={doReassign} disabled={!reassignTo} data-testid="task-reassign-submit"
                        className="px-3 py-1.5 text-xs bg-[#0f5c3d] text-white rounded-full font-semibold disabled:opacity-50">
                        Reassign
                      </button>
                    </div>
                  </div>
                )}

                {rejectOpen && (
                  <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="task-reject-panel">
                    <div className="text-xs font-semibold text-red-800 uppercase tracking-widest mb-2">Send back for rework</div>
                    <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Explain what needs to be fixed"
                      className="w-full mb-2 px-3 py-2 border border-red-300 rounded text-sm bg-white" rows={3} />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setRejectOpen(false)} className="px-3 py-1.5 text-xs text-[#0b1a2e]/70 hover:text-[#0b1a2e]">Cancel</button>
                      <button onClick={doReject} disabled={!rejectReason.trim()}
                        className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-full font-semibold disabled:opacity-50">
                        Send Back
                      </button>
                    </div>
                  </div>
                )}

                {task.description && (
                  <div className="mb-5">
                    <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-1.5">Description</div>
                    <div className="text-sm text-[#0b1a2e] whitespace-pre-wrap">{task.description}</div>
                  </div>
                )}

                {/* Checklist */}
                {(task.checklist || []).length > 0 && (
                  <div className="mb-5" data-testid="task-checklist">
                    <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-2">
                      Checklist ({task.checklist.filter((c) => c.done).length}/{task.checklist.length})
                    </div>
                    <div className="space-y-1">
                      {task.checklist.map((it) => (
                        <label key={it.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-[#f7fbf8] cursor-pointer">
                          <input type="checkbox" checked={!!it.done} onChange={() => doToggleChecklist(it.id)}
                            className="w-4 h-4 accent-[#0f5c3d]" />
                          <span className={`text-sm ${it.done ? 'line-through text-[#0b1a2e]/50' : 'text-[#0b1a2e]'}`}>
                            {it.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Attachments */}
                <div className="mb-5" data-testid="task-attachments">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest">
                      Files ({(task.attachments || []).length})
                    </div>
                    <label className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-[#0f5c3d] hover:text-[#0b1a2e] cursor-pointer">
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {uploading ? 'Uploading…' : 'Attach'}
                      <input type="file" multiple hidden onChange={(e) => doUpload(Array.from(e.target.files || []))}
                        data-testid="task-attach-input" />
                    </label>
                  </div>
                  {(task.attachments || []).length === 0 ? (
                    <div className="text-xs text-[#0b1a2e]/50 italic">No files attached yet.</div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {task.attachments.map((a) => {
                        const isImg = a.content_type && a.content_type.startsWith('image/');
                        return (
                          <div key={a.id} className="group relative bg-[#f7fbf8] border border-black/5 rounded-lg overflow-hidden">
                            {isImg ? (
                              <a href={a.url} target="_blank" rel="noreferrer">
                                <img src={a.thumb_url || a.url} alt={a.filename} className="w-full h-24 object-cover" loading="lazy" />
                              </a>
                            ) : (
                              <a href={a.url} target="_blank" rel="noreferrer" className="block p-3 text-center">
                                <Paperclip className="w-6 h-6 mx-auto mb-1 text-[#0b1a2e]/40" />
                                <div className="text-[11px] text-[#0b1a2e] truncate">{a.filename}</div>
                              </a>
                            )}
                            <button onClick={() => doDeleteAttachment(a.id)}
                              className="absolute top-1 right-1 p-1 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition"
                              aria-label="delete attachment">
                              <Trash2 className="w-3 h-3 text-red-600" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Ratings — visible to assignor, assignee, and admins */}
                {(user?.user_id === task.created_by || user?.user_id === task.assignee_id || ['super_admin', 'admin'].includes(user?.role)) && (
                  <div className="mb-6 pb-6 border-b border-black/5" data-testid="task-ratings">
                    <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-2 flex items-center gap-1">
                      <Star className="w-3.5 h-3.5" /> Ratings
                      {ratings.length > 0 && (
                        <span className="ml-auto text-[10px] font-normal text-[#0b1a2e]/60 normal-case tracking-normal">
                          Avg {(ratings.reduce((a, r) => a + r.score, 0) / ratings.length).toFixed(1)}★ · {ratings.length} rating{ratings.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2 mb-3">
                      {ratings.length === 0 ? (
                        <div className="text-xs text-[#0b1a2e]/50 italic">
                          {task.status === 'done' ? 'Not rated yet.' : 'Ratings appear after the task is completed.'}
                        </div>
                      ) : ratings.map((r) => (
                        <div key={r.id} className="p-2 bg-[#f7fbf8] rounded" data-testid="task-rating">
                          <div className="flex items-center gap-2 text-xs">
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map((n) => (
                                <Star key={n} className={`w-3.5 h-3.5 ${n <= r.score ? 'text-amber-500 fill-amber-500' : 'text-[#0b1a2e]/20'}`} />
                              ))}
                            </div>
                            <span className="font-semibold text-[#0b1a2e]">{r.rater_name}</span>
                            <span className="text-[#0b1a2e]/50">· {timeAgo(r.created_at)}</span>
                          </div>
                          {r.comment && <div className="mt-1 text-sm text-[#0b1a2e]">{r.comment}</div>}
                        </div>
                      ))}
                    </div>
                    {/* Assignor rating form (only when task done + user is creator/admin) */}
                    {(task.status === 'done' || task.status === 'review') &&
                     (user?.user_id === task.created_by || ['super_admin', 'admin'].includes(user?.role)) &&
                     task.assignee_id && task.assignee_id !== user?.user_id && (
                      <div className="p-3 bg-white border border-[#0f5c3d]/15 rounded">
                        <div className="text-xs font-semibold text-[#0b1a2e] mb-2">Rate this work</div>
                        <div className="flex items-center gap-1 mb-2">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} onClick={() => setRatingScore(n)} data-testid={`rate-star-${n}`}
                              className="p-0.5" aria-label={`Rate ${n} stars`}>
                              <Star className={`w-6 h-6 ${n <= ratingScore ? 'text-amber-500 fill-amber-500' : 'text-[#0b1a2e]/20 hover:text-amber-300'}`} />
                            </button>
                          ))}
                        </div>
                        <textarea value={ratingComment} onChange={(e) => setRatingComment(e.target.value)}
                          data-testid="rating-comment"
                          placeholder="Optional feedback for the assignee…"
                          className="w-full px-3 py-2 border border-slate-300 rounded text-sm" rows={2} />
                        <div className="mt-2 flex justify-end">
                          <button onClick={submitRating} disabled={ratingSaving} data-testid="rating-submit"
                            className="px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold disabled:opacity-50">
                            {ratingSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : 'Submit rating'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Comments */}
                <div data-testid="task-comments">
                  <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-3 flex items-center gap-2">
                    <MessageSquare className="w-3.5 h-3.5" />
                    Discussion ({(task.comments || []).length})
                  </div>                  <div className="space-y-3 mb-3">
                    {(task.comments || []).length === 0 ? (
                      <div className="text-xs text-[#0b1a2e]/50 italic">No comments yet — start the conversation.</div>
                    ) : task.comments.map((c) => (
                      <div key={c.id} className="flex gap-3 group" data-testid="task-comment">
                        <div className="w-8 h-8 rounded-full bg-[#0f5c3d] text-white text-xs flex items-center justify-center flex-shrink-0 font-semibold">
                          {(c.author_name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-xs mb-0.5 flex-wrap">
                            <span className="font-semibold text-[#0b1a2e]">{c.author_name}</span>
                            <span className="text-[#0b1a2e]/50">{timeAgo(c.created_at)}</span>
                            {Array.isArray(c.visible_to_ids) && (
                              <span
                                data-testid="comment-restricted-badge"
                                title={`Only ${(c.visible_to_ids || []).map((id) => userLookup[id]?.name || 'someone').join(', ') || 'the author'} can see this`}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wider">
                                🔒 Restricted · {(c.visible_to_ids || []).length + 1}
                              </span>
                            )}
                            {(c.author_id === user?.user_id || ['super_admin', 'admin'].includes(user?.role)) && (
                              <button onClick={() => doDeleteComment(c.id)} className="ml-auto opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-700" aria-label="delete">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          <div className="text-sm text-[#0b1a2e] whitespace-pre-wrap">{c.body}</div>
                          {c.author_id === user?.user_id && (c.read_by || []).filter((r) => r.user_id !== c.author_id).length > 0 && (
                            <div className="flex items-center gap-0.5 mt-1" data-testid="comment-read-dots"
                              title={`Read by ${(c.read_by || []).filter((r) => r.user_id !== c.author_id).map((r) => userLookup[r.user_id]?.name || 'someone').join(', ')}`}>
                              <span className="text-[9px] text-[#0b1a2e]/40 mr-0.5">Read</span>
                              {(c.read_by || []).filter((r) => r.user_id !== c.author_id).slice(0, 4).map((r) => {
                                const u = userLookup[r.user_id];
                                return (
                                  <span key={r.user_id} data-testid="comment-read-dot"
                                    className="w-4 h-4 rounded-full bg-[#0f5c3d] text-white text-[8px] font-bold flex items-center justify-center border border-white -ml-1 first:ml-0">
                                    {(u?.name || u?.email || '?').charAt(0).toUpperCase()}
                                  </span>
                                );
                              })}
                              {((c.read_by || []).filter((r) => r.user_id !== c.author_id).length - 4) > 0 && (
                                <span className="text-[9px] text-[#0b1a2e]/50 ml-0.5">
                                  +{(c.read_by || []).filter((r) => r.user_id !== c.author_id).length - 4}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* New comment */}
                  <div data-testid="task-comment-composer">
                    <div className="relative flex gap-2">
                      <div className="flex-1 relative">
                        <textarea
                          ref={commentRef}
                          value={comment}
                          onChange={(e) => {
                            const v = e.target.value;
                            setComment(v);
                            // Detect the @token immediately before the caret
                            const caret = e.target.selectionStart ?? v.length;
                            const before = v.slice(0, caret);
                            const m = before.match(/(?:^|\s)@([\w.@-]*)$/);
                            if (m) {
                              setMentionQuery({ text: m[1].toLowerCase(), start: before.length - m[1].length - 1 });
                              setMentionIdx(0);
                            } else {
                              setMentionQuery(null);
                            }
                          }}
                          placeholder="Write a comment… type @ to mention (Enter to send, Shift+Enter for newline)"
                          data-testid="task-comment-input"
                          onKeyDown={(e) => {
                            const filtered = mentionQuery
                              ? users.filter((u) => (u.name || u.email || '').toLowerCase().includes(mentionQuery.text)).slice(0, 8)
                              : [];
                            if (mentionQuery && filtered.length > 0 && ['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(e.key)) {
                              if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx((i) => (i + 1) % filtered.length); return; }
                              if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIdx((i) => (i - 1 + filtered.length) % filtered.length); return; }
                              if (e.key === 'Escape')    { e.preventDefault(); setMentionQuery(null); return; }
                              if (e.key === 'Enter' || e.key === 'Tab') {
                                e.preventDefault();
                                const u = filtered[mentionIdx];
                                const before = comment.slice(0, mentionQuery.start);
                                const afterStart = mentionQuery.start + 1 + mentionQuery.text.length;
                                const after = comment.slice(afterStart);
                                const handle = (u.email || '').split('@')[0] || u.name || '';
                                const insert = `@${handle} `;
                                const next = before + insert + after;
                                setComment(next);
                                setCommentMentions((prev) => Array.from(new Set([...prev, u.user_id])));
                                setMentionQuery(null);
                                // Move caret to after the inserted mention
                                setTimeout(() => {
                                  if (commentRef.current) {
                                    const pos = before.length + insert.length;
                                    commentRef.current.selectionStart = commentRef.current.selectionEnd = pos;
                                    commentRef.current.focus();
                                  }
                                }, 0);
                                return;
                              }
                            }
                            if (e.key === 'Enter' && !e.shiftKey && !mentionQuery) {
                              e.preventDefault();
                              doComment();
                            }
                          }}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
                          rows={2}
                        />
                        {mentionQuery && (() => {
                          const filtered = users
                            .filter((u) => (u.name || u.email || '').toLowerCase().includes(mentionQuery.text))
                            .slice(0, 8);
                          if (filtered.length === 0) return null;
                          return (
                            <div
                              data-testid="mention-popover"
                              className="absolute left-2 -top-1 -translate-y-full z-10 bg-white border border-[#0f5c3d]/25 rounded-lg shadow-lg w-64 max-h-64 overflow-y-auto">
                              <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-[#0b1a2e]/50 border-b border-black/5">Mention someone</div>
                              {filtered.map((u, i) => (
                                <button
                                  key={u.user_id}
                                  type="button"
                                  data-testid={`mention-option-${u.user_id}`}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    // Reuse the Enter/Tab insertion path
                                    const before = comment.slice(0, mentionQuery.start);
                                    const afterStart = mentionQuery.start + 1 + mentionQuery.text.length;
                                    const after = comment.slice(afterStart);
                                    const handle = (u.email || '').split('@')[0] || u.name || '';
                                    const insert = `@${handle} `;
                                    setComment(before + insert + after);
                                    setCommentMentions((prev) => Array.from(new Set([...prev, u.user_id])));
                                    setMentionQuery(null);
                                    setTimeout(() => commentRef.current?.focus(), 0);
                                  }}
                                  className={`w-full text-left px-2 py-1.5 flex items-center gap-2 text-sm ${i === mentionIdx ? 'bg-[#f7fbf8]' : 'hover:bg-[#f7fbf8]'}`}
                                >
                                  <span className="w-6 h-6 rounded-full bg-[#0f5c3d] text-white text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                                    {(u.name || u.email || '?').charAt(0).toUpperCase()}
                                  </span>
                                  <span className="flex-1 min-w-0 truncate text-[#0b1a2e]">{u.name || u.email}</span>
                                  {u.role && <span className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/50">{u.role}</span>}
                                </button>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                      <button onClick={doComment} disabled={!comment.trim()} data-testid="task-comment-submit"
                        className="px-4 py-2 bg-[#0f5c3d] text-white rounded-lg text-sm font-semibold disabled:opacity-50 self-start">
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                    {/* Visibility bar */}
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-[#0b1a2e]/50 uppercase tracking-widest text-[10px] font-semibold">Visible to</span>
                      <button type="button" onClick={() => setCommentVisibility('everyone')}
                        data-testid="visibility-everyone"
                        className={`px-2.5 py-1 rounded-full font-medium ${commentVisibility === 'everyone' ? 'bg-[#0f5c3d] text-white' : 'bg-white border border-[#0f5c3d]/20 text-[#0b1a2e]'}`}>
                        Everyone
                      </button>
                      <button type="button" onClick={() => setCommentVisibility('selected')}
                        data-testid="visibility-selected"
                        className={`px-2.5 py-1 rounded-full font-medium ${commentVisibility === 'selected' ? 'bg-[#0f5c3d] text-white' : 'bg-white border border-[#0f5c3d]/20 text-[#0b1a2e]'}`}>
                        Selected people
                      </button>
                      {commentMentions.length > 0 && (
                        <span className="text-[10px] text-[#0f5c3d]" data-testid="mention-chip-count">
                          {commentMentions.length} @mention{commentMentions.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    {commentVisibility === 'selected' && (
                      <div className="mt-2 p-2 bg-[#f7fbf8] rounded-lg border border-[#0f5c3d]/15 max-h-40 overflow-y-auto" data-testid="visibility-picker">
                        <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">
                          Who can see this comment? (@mentioned people auto-included)
                        </div>
                        {users.map((u) => {
                          const checked = commentVisibleTo.includes(u.user_id) || commentMentions.includes(u.user_id);
                          const forced = commentMentions.includes(u.user_id);
                          return (
                            <label key={u.user_id} className="flex items-center gap-2 py-0.5 px-1 hover:bg-white rounded cursor-pointer text-sm">
                              <input type="checkbox" checked={checked} disabled={forced}
                                onChange={() => setCommentVisibleTo((prev) => prev.includes(u.user_id)
                                  ? prev.filter((x) => x !== u.user_id)
                                  : [...prev, u.user_id])}
                                data-testid={`visible-to-${u.user_id}`}
                                className="w-4 h-4 accent-[#0f5c3d]" />
                              <span className="w-5 h-5 rounded-full bg-[#0f5c3d] text-white text-[9px] font-semibold flex items-center justify-center">
                                {(u.name || u.email || '?').charAt(0).toUpperCase()}
                              </span>
                              <span className="text-[#0b1a2e]">{u.name || u.email}</span>
                              {forced && <span className="ml-auto text-[9px] uppercase tracking-widest text-[#0f5c3d]">@mentioned</span>}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* RIGHT — meta panel */}
              <div className="p-6 bg-[#f7fbf8] space-y-4">
                <MetaRow icon={Flag} label="Status">
                  <select value={task.status} onChange={(e) => doChange({ status: e.target.value })}
                    data-testid="task-status-select"
                    className="text-sm bg-white border border-slate-300 rounded px-2 py-1 font-medium">
                    {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </MetaRow>

                <MetaRow icon={Flag} label="Priority">
                  <select value={task.priority} onChange={(e) => doChange({ priority: e.target.value })}
                    data-testid="task-priority-select"
                    className="text-sm bg-white border border-slate-300 rounded px-2 py-1 font-medium">
                    <option value="urgent">Urgent</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </MetaRow>

                <MetaRow icon={UserIcon} label="Assignee">
                  <div className="text-sm text-[#0b1a2e]">
                    {task.assignee_id ? (userLookup[task.assignee_id]?.name || userLookup[task.assignee_id]?.email || '—') : 'Unassigned'}
                  </div>
                </MetaRow>

                <MetaRow icon={UserIcon} label="Assigned by">
                  <div className="text-sm text-[#0b1a2e]">{task.created_by_name || '—'}</div>
                </MetaRow>

                <MetaRow icon={CalendarIcon} label="Due date">
                  <input type="date" value={(task.due_date || '').slice(0, 10)}
                    onChange={(e) => doChange({ due_date: e.target.value ? e.target.value + 'T09:00:00' : null })}
                    data-testid="task-due-date"
                    className="text-sm bg-white border border-slate-300 rounded px-2 py-1" />
                </MetaRow>

                <MetaRow icon={Clock} label="Start – End">
                  <div className="flex gap-1">
                    <input type="time" value={(task.start_time || '').slice(11, 16)}
                      onChange={(e) => doChange({ start_time: (task.due_date || new Date().toISOString().slice(0, 10)).slice(0, 10) + 'T' + e.target.value + ':00' })}
                      className="text-xs bg-white border border-slate-300 rounded px-1.5 py-1 w-24" />
                    <span className="text-xs self-center text-[#0b1a2e]/50">–</span>
                    <input type="time" value={(task.end_time || '').slice(11, 16)}
                      onChange={(e) => doChange({ end_time: (task.due_date || new Date().toISOString().slice(0, 10)).slice(0, 10) + 'T' + e.target.value + ':00' })}
                      className="text-xs bg-white border border-slate-300 rounded px-1.5 py-1 w-24" />
                  </div>
                </MetaRow>

                <MetaRow icon={ShieldCheck} label="Approval required">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!task.requires_approval}
                      onChange={(e) => doChange({ requires_approval: e.target.checked })}
                      className="w-4 h-4 accent-[#0f5c3d]" />
                    <span className="text-xs text-[#0b1a2e]/70">Assignor must approve completion</span>
                  </label>
                </MetaRow>

                {task.recurring && (
                  <MetaRow icon={Repeat} label="Recurring">
                    <div className="text-xs text-[#0b1a2e]">
                      Every {task.recurring.interval || 1} {task.recurring.freq}
                    </div>
                  </MetaRow>
                )}

                <MetaRow icon={Repeat} label="Recurring config">
                  <div className="flex flex-wrap gap-1">
                    <select value={task.recurring?.freq || ''}
                      onChange={(e) => {
                        const freq = e.target.value;
                        if (!freq) return doChange({ recurring: null });
                        doChange({ recurring: { freq, interval: task.recurring?.interval || 1, until: task.recurring?.until || null } });
                      }}
                      data-testid="recurring-freq"
                      className="text-xs bg-white border border-slate-300 rounded px-1.5 py-1">
                      <option value="">One-off</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    {task.recurring?.freq && (
                      <>
                        <input type="number" min="1" max="99"
                          value={task.recurring?.interval || 1}
                          onChange={(e) => doChange({ recurring: { ...task.recurring, interval: parseInt(e.target.value, 10) || 1 } })}
                          className="w-12 text-xs bg-white border border-slate-300 rounded px-1.5 py-1" />
                        <input type="date" value={(task.recurring?.until || '').slice(0, 10)}
                          onChange={(e) => doChange({ recurring: { ...task.recurring, until: e.target.value ? e.target.value + 'T23:59:59' : null } })}
                          className="text-xs bg-white border border-slate-300 rounded px-1.5 py-1"
                          title="End date (optional)" />
                      </>
                    )}
                  </div>
                </MetaRow>

                {typeof task.estimated_minutes === 'number' && (
                  <MetaRow icon={Clock} label="Estimated hours">
                    <input type="number" step="0.25" min="0.25"
                      defaultValue={Math.round((task.estimated_minutes / 60) * 100) / 100}
                      onBlur={(e) => {
                        const h = parseFloat(e.target.value);
                        if (h > 0) doChange({ estimated_minutes: Math.round(h * 60) });
                      }}
                      className="w-24 text-xs bg-white border border-slate-300 rounded px-1.5 py-1" />
                  </MetaRow>
                )}

                {(task.time_logs || []).length > 0 && (
                  <MetaRow icon={Clock} label="Time logged">
                    <div className="text-xs text-[#0b1a2e]">
                      {Math.round((task.time_logs || []).reduce((s, l) => s + (l.minutes || 0), 0) / 60 * 10) / 10}h across {task.time_logs.length} entries
                    </div>
                  </MetaRow>
                )}

                <MetaRow icon={Clock} label="Created">
                  <div className="text-xs text-[#0b1a2e]" data-testid="task-created-at">
                    {task.created_at ? new Date(task.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                  </div>
                </MetaRow>

                {(task.last_activity_at || task.updated_at) && String(task.last_activity_at || task.updated_at).slice(0, 16) !== String(task.created_at || '').slice(0, 16) && (
                  <MetaRow icon={Clock} label="Last activity">
                    <div className="text-xs text-[#0f5c3d] font-semibold" data-testid="task-last-activity">
                      {new Date(task.last_activity_at || task.updated_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                    </div>
                  </MetaRow>
                )}

                {/* History */}
                <div className="pt-3 border-t border-black/5">
                  <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-2">Activity</div>
                  <div className="space-y-1.5 text-xs">
                    {(task.history || []).slice().reverse().slice(0, 8).map((h, i) => (
                      <div key={i} className="text-[#0b1a2e]/70 flex items-start gap-1.5">
                        <ChevronRight className="w-3 h-3 mt-0.5 text-[#0f5c3d]/50 flex-shrink-0" />
                        <span>
                          <strong className="text-[#0b1a2e]">{h.actor_name || 'system'}</strong>
                          {' '}{formatHistoryAction(h)}
                          <span className="text-[#0b1a2e]/40 ml-1">· {timeAgo(h.at)}</span>
                          {h.reason && <div className="italic text-[#0b1a2e]/60 mt-0.5">"{h.reason}"</div>}
                        </span>
                      </div>
                    ))}
                    {(task.history || []).length === 0 && <div className="text-[#0b1a2e]/40 italic">No activity yet.</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MetaRow = ({ icon: Icon, label, children }) => (
  <div>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1">
      <Icon className="w-3 h-3" />
      <span>{label}</span>
    </div>
    <div className="pl-4">{children}</div>
  </div>
);

const formatHistoryAction = (h) => {
  const a = h.action || 'updated';
  if (a === 'reassigned') return 'reassigned this task';
  if (a === 'accepted') return 'accepted the task';
  if (a === 'completed') return 'marked done';
  if (a === 'submitted_for_approval') return 'submitted for approval';
  if (a === 'approved') return 'approved & closed';
  if (a === 'sent_back') return 'sent back for rework';
  if (a === 'status_changed') return `changed status ${h.from || '?'} → ${h.to || '?'}`;
  if (a === 'created') return 'created the task';
  return a;
};

export default TaskDetailDialog;
